import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { Layout } from 'react-grid-layout';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Global, css } from '@emotion/react';
import { AddCircleOutline as AddCircleOutlineIcon, MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { IconButton, InputBase, MenuItem, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import type { BlockConfig } from '@trinity/chat/types/dashboard-config';
import {
  useDashboardBuilder,
  useDashboardBuilderEditing,
  useDashboardConfig,
} from '@trinity/chat-components/containers/Dashboard/context';
import { ROW_HEIGHT, BLOCK_GAP, SHELL_COLLAPSED_HEIGHT, SHELL_HEADER_HEIGHT, HIDDEN_TITLE_HEADER_HEIGHT, HIDDEN_TITLE_COLLAPSED_HEIGHT, MIN_COL_WIDTH, getContentHeightUnits, getPreviewContentHeightUnits } from './dashboardLayoutUtils';
import { ThemedMenu } from './shared-styled';
import { WidgetCardShell } from './WidgetCardShell';
import type { WidgetInstance } from '@trinity/chat';
import { WidgetPalette } from './WidgetPalette';
import { useBuilderPalette } from './PaletteContext';

const ResponsiveGridLayout = WidthProvider(Responsive);

const GRID_BREAKPOINTS = { lg: 960, md: 720, sm: 480, xs: 320, xxs: 0 };

/**
 * Height of one widget row in pixels. Sections use this same unit so that
 * dragging a section taller by one snap = one widget row = one block row.
 */
/**
 * Returns the RGL h-value (height in row units) for a block.
 *
 * Each block row can span more than one widget-row unit when it contains a
 * section with multiple inner rows. `getContentHeightUnits` sums those heights,
 * adding a trailing empty row when the last occupied row is fully filled.
 * block.minRows is respected as an explicit user-set minimum.
 */
function getBlockRowCount(block: BlockConfig): number {
  return Math.max(block.minRows ?? 1, getContentHeightUnits(block.items, block.w ?? 3));
}

/**
 * Like `getBlockRowCount` but for preview/read-only mode.
 * Uses only actual widget content — no trailing rows for drop targets,
 * no `minRows` padding (empty rows serve no purpose in preview).
 */
function getPreviewBlockRowCount(block: BlockConfig): number {
  return Math.max(1, getPreviewContentHeightUnits(block.items, block.w ?? 3));
}

/**
 * Pack blocks into a react-grid-layout Layout array.
 *
 * Because RGL runs in pixel mode (rowHeight=1, no vertical margin), the `h`
 * value IS the pixel height of the item.  BLOCK_GAP is baked into each item
 * so there is visual breathing room between blocks without relying on RGL margin.
 *
 * Horizontal position (x) is stored per block; vertical position (y) is derived
 * by tracking per-column heights — blocks in the same columns share the same y
 * baseline, enabling true side-by-side placement.
 */
function packBlocksToLayout(blocks: BlockConfig[], collapsedIds: Set<string>, outerCols: number, forPreview = false): Layout[] {
  const colHeights: number[] = Array(outerCols).fill(0);
  const result: Layout[] = [];

  for (const block of blocks) {
    const isCollapsed = collapsedIds.has(block.id);
    const isHidden = block.hideTitle ?? false;
    const headerH = isHidden ? HIDDEN_TITLE_HEADER_HEIGHT : SHELL_HEADER_HEIGHT;
    const collapsedH = isHidden ? HIDDEN_TITLE_COLLAPSED_HEIGHT : SHELL_COLLAPSED_HEIGHT;
    const rowCount = forPreview ? getPreviewBlockRowCount(block) : getBlockRowCount(block);
    const contentH = isCollapsed
      ? collapsedH
      : headerH + rowCount * ROW_HEIGHT + BLOCK_GAP;
    const h = contentH + BLOCK_GAP;

    const x = block.x ?? 0;
    const w = block.w ?? 3;

    // y = max height of all columns this block spans (avoids overlap)
    let y = 0;
    for (let c = x; c < x + w && c < outerCols; c++) {
      y = Math.max(y, colHeights[c]);
    }
    // Advance column heights for every spanned column
    for (let c = x; c < x + w && c < outerCols; c++) {
      colHeights[c] = y + h;
    }

    result.push({
      i: block.id,
      x,
      y,
      w,
      h,
      minH: headerH + ROW_HEIGHT + BLOCK_GAP,
      minW: 1,
      maxW: outerCols,
      isResizable: !isCollapsed,
    });
  }
  return result;
}

type Props = {
  title?: string;
  onTitleChange?: (title: string) => void;
  /** When true, disables dragging, resizing, and the AddBar — for view-only rendering. */
  readOnly?: boolean;
};

/**
 * BuilderCanvas  the react-grid-layout-backed grid where blocks are placed,
 * dragged, and resized during dashboard editing.
 *
 * Layout is derived from block order + width. Height is always 1 row.
 * Only east (width) resize is allowed  height is fixed.
 */
export function BuilderCanvas({ title, onTitleChange, readOnly = false }: Props): React.ReactElement {
  const { config } = useDashboardConfig();
  const { reorderBlocks, setBlockRows, setBlockWidth, updateBlocksFromLayout } = useDashboardBuilderEditing();

  const outerCols = config.outerCols ?? 3;
  const gridCols = { lg: outerCols, md: outerCols, sm: outerCols, xs: outerCols, xxs: outerCols };

  // Track which blocks are collapsed.  Keyed by block id.
  // Initialize from blocks with defaultCollapsed: true.
  const [collapsedBlockIds, setCollapsedBlockIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const block of config.blocks) {
      if (block.collapsible && block.defaultCollapsed) initial.add(block.id);
    }
    return initial;
  });

  const toggleCollapse = useCallback((blockId: string) => {
    setCollapsedBlockIds(prev => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  }, []);

  const rawLayout = packBlocksToLayout(config.blocks, collapsedBlockIds, outerCols, readOnly);
  // In readOnly/preview, mark every item static so RGL can't drag or resize them
  // regardless of any per-item isResizable flags.
  const layout = readOnly ? rawLayout.map(item => ({ ...item, static: true })) : rawLayout;

  const handleLayoutChange = useCallback((newLayout: Layout[]) => {
    // Only reorder — x positions are persisted in handleDragStop instead.
    const sortedIds = [...newLayout]
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map(item => item.i);
    const currentOrder = config.blocks.map(b => b.id);
    if (sortedIds.join(',') !== currentOrder.join(',')) {
      reorderBlocks(sortedIds);
    }
  }, [config.blocks, reorderBlocks]);

  /**
   * Called when a drag interaction ends.  Persists each block's new column (x)
   * and re-establishes the visual order so packBlocksToLayout reproduces the
   * same layout on the next render.
   */
  const handleDragStop = useCallback((newLayout: Layout[]) => {
    const xMap: Record<string, number> = {};
    newLayout.forEach(item => { xMap[item.i] = item.x; });
    const sortedIds = [...newLayout]
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map(item => item.i);
    updateBlocksFromLayout(sortedIds, xMap);
  }, [updateBlocksFromLayout]);

  // Track the last snapped row count per block during an active resize so we only
  // call setBlockRows (and trigger a React re-render) when the count *changes*.
  const liveRowsRef = useRef<Record<string, number>>({});

  /**
   * Called continuously during drag — snaps the block height to whole ROW_HEIGHT increments
   * AND updates block.minRows live so that WidgetCardShell immediately renders placeholder
   * rows as the user drags down.
   */
  const handleResize = useCallback((_layout: Layout[], _oldItem: Layout, newItem: Layout, placeholder: Layout) => {
    const block = config.blocks.find(b => b.id === newItem.i);
    const headerH = (block?.hideTitle ?? false) ? HIDDEN_TITLE_HEADER_HEIGHT : SHELL_HEADER_HEIGHT;
    const rawContentPx = newItem.h - BLOCK_GAP - headerH;
    const rowCount = Math.max(1, Math.round(rawContentPx / ROW_HEIGHT));
    const snappedH = headerH + rowCount * ROW_HEIGHT + BLOCK_GAP;
    newItem.h = snappedH;
    placeholder.h = snappedH;
    // Update minRows live so WidgetCardShell renders new placeholder rows during drag
    const prev = liveRowsRef.current[newItem.i];
    if (prev !== rowCount) {
      liveRowsRef.current[newItem.i] = rowCount;
      setBlockRows(newItem.i, rowCount);
    }
  }, [config.blocks, setBlockRows]);

  const handleResizeStop = useCallback((_layout: Layout[], _oldItem: Layout, newItem: Layout) => {
    // Clear the live-tracking entry for this block
    delete liveRowsRef.current[newItem.i];
    const block = config.blocks.find(b => b.id === newItem.i);
    // Height: newItem.h is in pixels; convert back to widget-row units.
    const headerH = (block?.hideTitle ?? false) ? HIDDEN_TITLE_HEADER_HEIGHT : SHELL_HEADER_HEIGHT;
    const contentPx = newItem.h - BLOCK_GAP - headerH;
    setBlockRows(newItem.i, Math.max(1, Math.round(contentPx / ROW_HEIGHT)));
    // Width: snap to valid column count within the outer grid.
    const newW = Math.max(1, Math.min(outerCols, newItem.w));
    if (newW !== (block?.w ?? 3)) {
      setBlockWidth(newItem.i, newW);
    }
  }, [config.blocks, setBlockRows, setBlockWidth]);

  return (
    <>
      {!readOnly && <RglGlobalStyles />}
      <CanvasContainer readOnly={readOnly} outerCols={outerCols}>
        <TitleBlock title={title} onTitleChange={onTitleChange} readOnly={readOnly} />
        <ResponsiveGridLayout
          className="layout"
          layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
          breakpoints={GRID_BREAKPOINTS}
          cols={gridCols}
          rowHeight={1}
          margin={[12, 0]}
          containerPadding={[36, 16]}
          draggableHandle=".drag-handle"
          onLayoutChange={handleLayoutChange}
          onDragStop={handleDragStop}
          onResize={handleResize}
          onResizeStop={handleResizeStop}
          isDroppable={false}
          isDraggable={!readOnly}
          isResizable={!readOnly}
          resizeHandles={['s', 'e', 'se']}
        >
          {config.blocks.map(block => (
            <div key={block.id}>
              <WidgetCardShell
                blockId={block.id}
                title={block.title}
                hideTitle={block.hideTitle ?? false}
                blockCols={(block.w ?? 3)}
                collapsed={collapsedBlockIds.has(block.id)}
                onToggleCollapse={() => toggleCollapse(block.id)}
                readOnly={readOnly}
                collapsible={block.collapsible ?? false}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
        {!readOnly && <AddBar />}
      </CanvasContainer>
    </>
  );
}

// ─── TitleBlock ───────────────────────────────────────────────────────────────

type TitleBlockProps = {
  title?: string;
  onTitleChange?: (title: string) => void;
  /** When true, renders a read-only Typography heading instead of an editable input. */
  readOnly?: boolean;
};

function TitleBlock({ title = '', onTitleChange, readOnly = false }: TitleBlockProps): React.ReactElement {
  const [localTitle, setLocalTitle] = useState(title);
  const menuId = useId();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const { config, setConfig } = useDashboardConfig();
  const hideTitle = config.hideTitle ?? false;

  // Keep localTitle in sync when the title prop changes (must be before any early return)
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  // Preview mode: render as clean read-only heading
  if (readOnly) {
    if (hideTitle || !title) return <></> as React.ReactElement;
    return (
      <Typography
        variant="h4"
        fontWeight={700}
        sx={{ margin: '32px 36px 12px', color: 'text.primary' }}
      >
        {title}
      </Typography>
    );
  }

  const handleBlur = () => {
    if (localTitle !== title) {
      onTitleChange?.(localTitle);
    }
  };

  const handleToggleHideTitle = () => {
    setMenuAnchor(null);
    setConfig(prev => ({ ...prev, hideTitle: !(prev.hideTitle ?? false) }));
  };

  return (
    <TitleBlockRoot hideTitle={hideTitle}>
      <TitleBlockInput
        hideTitle={hideTitle}
        value={localTitle}
        onChange={e => setLocalTitle(e.target.value)}
        onBlur={handleBlur}
        placeholder="Dashboard title..."
        inputProps={{ 'aria-label': 'Dashboard title' }}
      />
      <IconButton
        size="small"
        onClick={e => setMenuAnchor(e.currentTarget)}
        aria-label="Dashboard title actions"
        aria-controls={menuAnchor ? menuId : undefined}
        aria-haspopup="true"
        sx={{ flexShrink: 0, padding: hideTitle ? '1px' : undefined }}
      >
        <MoreHorizIcon fontSize={hideTitle ? 'inherit' : 'small'} />
      </IconButton>
      <ThemedMenu
        id={menuId}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        MenuListProps={{ dense: true }}
      >
        <MenuItem onClick={handleToggleHideTitle}>
          {hideTitle ? 'Show title' : 'Hide title'}
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onTitleChange?.(''); setLocalTitle(''); }}>
          Clear title
        </MenuItem>
      </ThemedMenu>
    </TitleBlockRoot>
  );
}

// ─── AddBar ───────────────────────────────────────────────────────────────────

function AddBar(): React.ReactElement {
  const { addBlock, addBlockWithWidget } = useDashboardBuilderEditing();
  const { setPendingSlot, setPendingWidgetType, setSidebarPanel } = useDashboardBuilder();
  const palette = useBuilderPalette();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const handleAddBlock = () => {
    setMenuOpen(false);
    addBlock();
  };

  const handleAddWidget = () => {
    setMenuOpen(false);
    setPaletteOpen(true);
  };

  /** After palette selection, open the create-widget sidebar with a sentinel slot.
   *  The sidebar's handleCreate will detect blockId='__new__' and call addBlockWithWidget. */
  const handleWidgetSelect = (type: string) => {
    setPaletteOpen(false);
    setPendingSlot({ blockId: '__new__', row: 1, slot: 1 });
    setPendingWidgetType(type);
    setSidebarPanel('create-widget');
  };

  const handleLibraryWidgetSelect = (widget: WidgetInstance) => {
    if (!palette) return;
    const { type, config } = palette.toPageWidget(widget);
    addBlockWithWidget(type, config);
  };

  return (
    <>
      <AddBarRoot ref={anchorRef} onClick={() => setMenuOpen(prev => !prev)} role="button" tabIndex={0}>
        <AddCircleOutlineIcon sx={theme => ({ color: theme.palette.primary.main, fontSize: 20 })} />
        <Typography variant="body2" fontWeight={600} color="primary">
          Add
        </Typography>
      </AddBarRoot>
      <ThemedMenu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        MenuListProps={{ dense: true }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={handleAddBlock}>Add Block</MenuItem>
        <MenuItem onClick={handleAddWidget}>Add Widget</MenuItem>
      </ThemedMenu>
      <WidgetPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        hideSections
        onSelect={handleWidgetSelect}
        onSelectLibraryWidget={handleLibraryWidgetSelect}
      />
    </>
  );
}

//  react-grid-layout CSS injected via Emotion to avoid external CSS imports 

function RglGlobalStyles(): React.ReactElement {
  const theme = useTheme();
  return (
    <Global styles={css`
      .react-grid-layout {
        position: relative;
        transition: height 200ms ease;
      }
      .react-grid-item {
        transition: all 200ms ease;
        transition-property: left, top, width, height;
        box-sizing: border-box;
      }
      .react-grid-item.cssTransforms {
        transition-property: transform, width, height;
      }
      .react-grid-item.resizing {
        will-change: width, height;
        z-index: 1;
      }
      .react-grid-item.react-draggable-dragging {
        will-change: transform;
        z-index: 3;
        transition: none;
      }
      .react-grid-item.react-grid-placeholder {
        background: ${theme.palette.primary.main}40;
        border: 1px dashed ${theme.palette.primary.main}80;
        border-radius: 4px;
        z-index: 2;
        user-select: none;
      }

      /* ── Block resize handles ── */
      /* RGL injects .react-resizable-handle-* elements as direct children of
         the grid item wrapper div. Override RGL's default background-image
         handles with edge strips that match the widget/section resize style. */
      .react-grid-item > .react-resizable-handle {
        position: absolute;
        background-image: none;
        transition: background-color 150ms, box-shadow 150ms;
      }
      /* South (height) — full-width bottom strip */
      .react-grid-item > .react-resizable-handle-s,
      .react-grid-item > .react-resizable-handle-se {
        bottom: 0;
        left: 0;
        right: 0;
        height: 10px;
        width: auto;
        cursor: s-resize;
      }
      /* East (width) — full-height right strip */
      .react-grid-item > .react-resizable-handle-e,
      .react-grid-item > .react-resizable-handle-se {
        top: 0;
        right: 0;
        bottom: 0;
        width: 10px;
        height: auto;
        cursor: e-resize;
      }
      /* SE corner — small square, highest specificity so it stays in corner */
      .react-grid-item > .react-resizable-handle-se {
        top: auto;
        left: auto;
        bottom: 0;
        right: 0;
        width: 14px;
        height: 14px;
        cursor: se-resize;
      }
      /* Hover highlight — blue tint + inset shadow on active edge */
      .react-grid-item:hover > .react-resizable-handle-s:not(.react-resizable-handle-se) {
        background-color: ${theme.palette.primary.main}1A;
        box-shadow: inset 0 -3px 0 0 ${theme.palette.primary.main};
      }
      .react-grid-item:hover > .react-resizable-handle-e:not(.react-resizable-handle-se) {
        background-color: ${theme.palette.primary.main}1A;
        box-shadow: inset -3px 0 0 0 ${theme.palette.primary.main};
      }
      .react-grid-item:hover > .react-resizable-handle-se {
        background-color: ${theme.palette.primary.main}30;
        box-shadow: inset -3px -3px 0 0 ${theme.palette.primary.main};
        border-radius: 0 0 10px 0;
      }
    `} />
  );
}

//  Styled components 

const CanvasContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'readOnly' && prop !== 'outerCols',
})<{ readOnly?: boolean; outerCols?: number }>(({ theme, readOnly, outerCols = 3 }) => ({
  position: 'relative',
  '& > .react-grid-layout .react-grid-item': {
    '& > div': {
      height: '100%',
    },
  },
  ...(readOnly ? {
    // Preview: clean white page, scrolls as a whole via parent container.
    // minWidth ensures columns don't compress below MIN_COL_WIDTH when there are
    // many columns — the parent ScrollArea handles horizontal overflow.
    flex: 'none',
    overflowY: 'visible',
    minWidth: `${outerCols * MIN_COL_WIDTH}px`,
    backgroundColor: theme.palette.background.paper,
    margin: 0,
    // Hide RGL resize handle elements — they're injected into the DOM even when static=true
    '& .react-resizable-handle': {
      display: 'none',
    },
  } : {
    // Edit mode: dotted grid canvas with inner scroll.
    // minWidth mirrors the renderer so high column counts scroll horizontally
    // rather than compressing columns below MIN_COL_WIDTH.
    flex: 1,
    minWidth: `${outerCols * MIN_COL_WIDTH}px`,
    overflowY: 'auto',
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#F0F2F5',
    backgroundImage: `radial-gradient(circle, ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'} 1.7px, transparent 1.7px)`,
    backgroundSize: '24px 24px',
    margin: '5px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
  }),
}));

const TitleBlockRoot = styled('div', {
  shouldForwardProp: prop => prop !== 'hideTitle',
})<{ hideTitle: boolean }>(({ theme, hideTitle }) => ({
  margin: '16px 36px 8px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  borderRadius: '12px',
  cursor: 'text',
  ...(hideTitle ? {
    padding: '2px 4px 2px 8px',
    height: '24px',
    backgroundColor: `${theme.palette.primary.main}18`,
    backdropFilter: 'blur(4px)',
    border: `1px solid ${theme.palette.primary.light1}`,
  } : {
    padding: theme.spacing(1.5, 1, 1.5, 2),
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  }),
}));

const TitleBlockInput = styled(InputBase, {
  shouldForwardProp: prop => prop !== 'hideTitle',
})<{ hideTitle: boolean }>(({ theme, hideTitle }) => ({
  flex: 1,
  borderBottom: '2px solid transparent',
  transition: 'border-color 150ms',
  '&.Mui-focused': {
    borderBottomColor: theme.palette.text.disabled,
  },
  ...(hideTitle ? {
    marginRight: '26px',
    '& input': {
      padding: 0,
      cursor: 'text',
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 700,
      color: theme.palette.text.primary,
      lineHeight: 1,
    },
  } : {
    marginRight: '30px',
    '& input': {
      fontSize: theme.typography.h5.fontSize,
      fontWeight: 700,
      padding: 0,
      cursor: 'text',
      color: theme.palette.text.primary,
    },
  }),
}));

const AddBarRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.75),
  margin: '8px 36px 20px',
  padding: theme.spacing(1.25),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'background-color 150ms',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : theme.palette.grey[50],
  },
}));
